// API Base URL - Production ve Development için dinamik
// getApiBaseUrl function
const getApiBaseUrl = () => {
  // Window.ENV'den API URL'i al (production build'de)
  if (window.ENV && window.ENV.API_URL) {
    return window.ENV.API_URL; // Olduğu gibi kullan
  }

  // Environment variable varsa onu kullan
  if (process.env.REACT_APP_API_URL) {
    // Use as full base; only strip trailing slash if exists
    let url = process.env.REACT_APP_API_URL;
    if (url.endsWith('/')) {
      url = url.slice(0, -1);
    }
    return url;
  }

  const isLocalDevServer =
    process.env.NODE_ENV === 'development' || window.location.port === '3000';

  // React dev server (localhost veya ağ IP'si) → Django :8000
  if (isLocalDevServer) {
    const host = window.location.hostname || 'localhost';
    return `http://${host}:8000/api`;
  }

  // Production'da current protocol'ü kullan (HTTPS ise HTTPS, HTTP ise HTTP)
  if (window.location.hostname === 'app.akkahotels.com') {
    const protocol = window.location.protocol;
    return `${protocol}//${window.location.hostname}/api`;
  }

  // Diğer production hostname'ler için current protocol'ü kullan
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return `${protocol}//${hostname}/api`;
};

export const API_BASE_URL = getApiBaseUrl();
const FINAL_API_URL = API_BASE_URL;

// Token yönetimi için yardımcı fonksiyonlar
export const tokenManager = {
  setTokens: (tokens) => {
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
  },

  getAccessToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),

  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_user');
  },

  isAuthenticated: () => !!localStorage.getItem('access_token')
};

// Fetch wrapper fonksiyonu
export const apiRequest = async (endpoint, methodOrOptions = {}, bodyData = null) => {
  const url = `${FINAL_API_URL}${endpoint}`;
  const token = tokenManager.getAccessToken();

  // Eğer methodOrOptions string ise (eski kullanım: 'GET', 'POST' vb.)
  // veya bodyData varsa, options object'e çevir
  let options = {};
  if (typeof methodOrOptions === 'string') {
    // String method geçilmişse
    options.method = methodOrOptions;
    if (bodyData !== null) {
      // FormData ise olduğu gibi kullan, değilse JSON'a çevir
      options.body = bodyData instanceof FormData ? bodyData : JSON.stringify(bodyData);
    }
  } else {
    // Options object geçilmişse
    options = methodOrOptions;
    // Eğer body yoksa ve bodyData varsa ekle
    if (!options.body && bodyData !== null) {
      options.body = bodyData instanceof FormData ? bodyData : JSON.stringify(bodyData);
    }
  }

  const config = {
    method: options.method || 'GET', // Default GET
    cache: 'no-store',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  // Body varsa ekle
  if (options.body) {
    config.body = options.body;
  }

  // Diğer options'ları ekle (method ve body hariç)
  Object.keys(options).forEach(key => {
    if (key !== 'method' && key !== 'body' && key !== 'headers') {
      config[key] = options[key];
    }
  });

  // FormData değilse Content-Type ekle
  if (!(config.body instanceof FormData) && config.body) {
    config.headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(url, config);

    // Token süresi dolmuşsa refresh token ile yenile
    if (response.status === 401 && token) {
      const refreshSuccess = await refreshAccessToken();
      if (refreshSuccess) {
        // Yeni token ile tekrar dene
        config.headers.Authorization = `Bearer ${tokenManager.getAccessToken()}`;
        const retryResponse = await fetch(url, config);
        return await handleResponse(retryResponse);
      } else {
        // Refresh başarısızsa logout
        tokenManager.clearTokens();
        window.location.href = '/';
        return null;
      }
    }

    return await handleResponse(response);
  } catch (error) {
    if (error.message && error.message !== 'Network error occurred') {
      throw error;
    }
    throw new Error('Network error occurred');
  }
};

// Response handler
const handleResponse = async (response) => {
  if (response.status === 204) {
    return { success: true };
  }

  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();

    if (!response.ok) {
      const flattenErrors = (errors) => {
        if (!errors || typeof errors !== 'object') return '';
        const parts = [];
        for (const val of Object.values(errors)) {
          if (Array.isArray(val)) {
            val.forEach((item) => {
              if (item != null && String(item).trim()) parts.push(String(item).trim());
            });
          } else if (val != null && typeof val === 'object') {
            const nested = flattenErrors(val);
            if (nested) parts.push(nested);
          } else if (val != null && String(val).trim()) {
            parts.push(String(val).trim());
          }
        }
        return parts.join(' ');
      };
      const detail = flattenErrors(data.errors);
      const base = data.message || `HTTP error! status: ${response.status}`;
      const message = detail ? `${base}: ${detail}` : base;
      throw new Error(message);
    }

    return data;
  } else {
    if (!response.ok) {
      // HTML error page geldiğinde text olarak oku
      try {
        await response.text();
        throw new Error(`Server error (${response.status}): ${response.statusText}`);
      } catch (textError) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }
    }
    return response;
  }
};

// Refresh token fonksiyonu (medya indirme gibi apiRequest dışı fetch'lerde de kullanılır)
export const refreshAccessToken = async () => {
  const refreshToken = tokenManager.getRefreshToken();

  if (!refreshToken) return false;

  try {
    const response = await fetch(`${FINAL_API_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      tokenManager.setTokens({
        access: data.access,
        refresh: refreshToken
      });
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
};

// Auth API fonksiyonları
export const authAPI = {
  // Login
  login: async (email, password) => {
    try {
      const response = await apiRequest('/auth/login/', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.success && response.tokens) {
        tokenManager.setTokens(response.tokens);

        // Kullanıcı bilgisini localStorage'a kaydet
        localStorage.setItem('current_user', JSON.stringify(response.user));

        return {
          success: true,
          user: response.user,
          tokens: response.tokens
        };
      } else {
        return {
          success: false,
          message: response.message || 'Giriş başarısız'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Giriş sırasında bir hata oluştu'
      };
    }
  },

  // Logout
  logout: async () => {
    const refreshToken = tokenManager.getRefreshToken();

    try {
      await apiRequest('/auth/logout/', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (error) {
      // Silent error
    } finally {
      tokenManager.clearTokens();
    }
  },

  // Register
  register: async (userData) => {
    try {
      const response = await apiRequest('/auth/register/', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      if (response.success && response.tokens) {
        tokenManager.setTokens(response.tokens);

        // Kullanıcı bilgisini localStorage'a kaydet
        localStorage.setItem('current_user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Kayıt sırasında bir hata oluştu'
      };
    }
  },

  // Test authentication
  testAuth: async () => {
    try {
      const response = await apiRequest('/auth/test-auth/');
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Authentication test failed'
      };
    }
  },

  // Profil bilgilerini getir
  getProfile: async () => {
    return await apiRequest('/auth/profile/');
  },

  // Token doğrula
  verifyToken: async () => {
    try {
      const response = await apiRequest('/auth/token/verify/', {
        method: 'GET',
      });

      if (response.success) {
        // Kullanıcı bilgisini localStorage'a güncelle
        localStorage.setItem('current_user', JSON.stringify(response.user));

        return {
          success: true,
          user: response.user
        };
      } else {
        return { success: false };
      }
    } catch (error) {
      return { success: false };
    }
  },

  // Şifre değiştir
  changePassword: async (passwordData) => {
    return await apiRequest('/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  },

  // Şifremi unuttum - email gönder
  forgotPassword: async (email) => {
    return await apiRequest('/auth/forgot-password/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Şifre sıfırlama - token ile yeni şifre
  resetPassword: async (token, newPassword, newPasswordConfirm) => {
    return await apiRequest('/auth/reset-password/', {
      method: 'POST',
      body: JSON.stringify({
        token,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      }),
    });
  },

  // Profil güncelle
  updateProfile: async (profileData) => {
    return await apiRequest('/auth/profile/', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }
};

// Users API fonksiyonları
export const usersAPI = {
  // Kullanıcı listesi
  getUsers: async () => {
    return await apiRequest('/auth/users/');
  },

  // Kullanıcı arama
  searchUsers: async (query) => {
    return await apiRequest(`/auth/users/search/?q=${encodeURIComponent(query)}`);
  },

  // Kullanıcıyı ID ile getir (backend destekliyorsa)
  getUserById: async (userId) => {
    return await apiRequest(`/auth/users/${userId}/`);
  }
};

// Reference data API fonksiyonları
export const referenceAPI = {
  // Hotel listesi
  getHotels: async () => {
    return await apiRequest('/auth/hotels/');
  },

  // Departman listesi
  getDepartments: async (hotelId = null) => {
    const query = hotelId ? `?hotel_id=${hotelId}` : '';
    return await apiRequest(`/auth/departments/${query}`);
  },

  // Rol listesi
  getRoles: async () => {
    return await apiRequest('/auth/roles/');
  },

  // Öncelik listesi
  getPriorities: async () => {
    return await apiRequest('/auth/priorities/');
  },

  // Projeye göre departman bazında kullanıcıları getir
  getUsersByProjectDepartment: async (projectId) => {
    return await apiRequest(`/auth/users/by_project_department/?project_id=${projectId}`);
  }
};

// Projects API fonksiyonları
// projectsAPI.getProjects method inside projectsAPI object
export const projectsAPI = {
  // Proje listesi
  getProjects: async (departmentId = null, hotelId = null) => {
    let url = '/projects/';
    const params = new URLSearchParams();

    if (departmentId) {
      params.append('department_id', departmentId);
    }

    if (hotelId) {
      params.append('hotel_id', hotelId);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await apiRequest(url);
    return response;
  },

  // Proje oluştur
  createProject: async (projectData) => {
    return await apiRequest('/projects/create/', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  },

  // Debug - Projeleri debug et
  debugProjects: async (departmentId = null, hotelId = null) => {
    let url = '/projects/debug/';
    const params = new URLSearchParams();

    if (departmentId) {
      params.append('department_id', departmentId);
    }

    if (hotelId) {
      params.append('hotel_id', hotelId);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await apiRequest(url);

    return response;
  }
};

export const ticketsAPI = {
  // Tüm ticket'ları getir
  getTickets: async () => {
    // Cache busting için timestamp ekle
    const timestamp = Date.now();
    const response = await apiRequest(`/tickets/?_t=${timestamp}`);
    return response;
  },

  // Sadece kullanıcının kendi ticket'larını getir (Talepler ekranı için)
  getMyTickets: async () => {
    // Cache busting için timestamp ekle ve my_tickets parametresi ekle
    const timestamp = Date.now();
    const response = await apiRequest(`/tickets/?my_tickets=true&_t=${timestamp}`);
    return response;
  },

  // Tek ticket detayı (alt ticket listesi ve parent_ticket dahil)
  getTicket: async (ticketId) => {
    const timestamp = Date.now();
    return await apiRequest(`/tickets/${ticketId}/?_t=${timestamp}`);
  },

  // Ticket geçmişi (History sekmesi)
  getTicketActivity: async (ticketId) => {
    const timestamp = Date.now();
    return await apiRequest(`/tickets/${ticketId}/activity/?_t=${timestamp}`);
  },

  // Proje bazlı ticket'ları getir
  getTicketsByProject: async (projectId, isBoard = false) => {
    // Cache busting için timestamp ekle
    const timestamp = Date.now();
    const boardParam = isBoard ? '&board=true' : '';
    const response = await apiRequest(`/tickets/by_project/?project_id=${projectId}${boardParam}&_t=${timestamp}`);
    return response;
  },

  // Tüm status'ları getir
  getStatuses: async () => {
    return await apiRequest('/tickets/statuses/');
  },

  // Projeye göre status'ları getir
  getStatusesByProject: async (projectId, isBoard = false) => {
    const boardParam = isBoard ? '&board=true' : '';
    return await apiRequest(`/tickets/statuses_by_project/?project_id=${projectId}${boardParam}`);
  },

  // Status oluştur
  createStatus: async (statusData) => {
    return await apiRequest('/tickets/statuses/create/', {
      method: 'POST',
      body: JSON.stringify(statusData),
    });
  },

  // Status güncelle
  updateStatus: async (statusData) => {
    return await apiRequest('/tickets/statuses/update/', {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  },

  // Status sil
  deleteStatus: async (statusId) => {
    return await apiRequest(`/tickets/statuses/delete/?id=${statusId}`, {
      method: 'DELETE',
    });
  },

  // Ticket oluştur
  createTicket: async (ticketData) => {
    // FormData ise direkt gönder, değilse JSON olarak gönder
    const body = ticketData instanceof FormData ? ticketData : JSON.stringify(ticketData);

    return await apiRequest('/tickets/', {
      method: 'POST',
      body: body,
    });
  },

  // Ticket güncelle
  updateTicket: async (ticketId, ticketData) => {
    return await apiRequest(`/tickets/${ticketId}/`, {
      method: 'PATCH',
      body: JSON.stringify(ticketData),
    });
  },

  // Ticket sil
  deleteTicket: async (ticketId) => {
    return await apiRequest(`/tickets/${ticketId}/`, {
      method: 'DELETE',
    });
  },

  // Debug: Tüm ticket'ları listele
  debugTickets: async () => {
    console.log('debugTickets called but disabled to prevent log spam');
    return { success: true, data: [] };
  },

  // Ticket'a dosya yükle
  uploadAttachments: async (ticketId, formData) => {
    return await apiRequest(`/tickets/${ticketId}/attachments/`, {
      method: 'POST',
      body: formData,
    });
  },

  // Admin: iş yapanlar raporu (JSON önizleme)
  getWorkReport: async ({ date_from, date_to, assigned_to = [] } = {}) => {
    const params = new URLSearchParams();
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    if (Array.isArray(assigned_to) && assigned_to.length) {
      params.append('assigned_to', assigned_to.join(','));
    }
    return await apiRequest(`/tickets/work-report/?${params.toString()}`);
  },

  // Admin: iş yapanlar Excel indirme
  downloadWorkReportExcel: async ({ date_from, date_to, assigned_to = [] } = {}) => {
    const params = new URLSearchParams();
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    params.append('format', 'xlsx');
    if (Array.isArray(assigned_to) && assigned_to.length) {
      params.append('assigned_to', assigned_to.join(','));
    }

    const token = tokenManager.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/tickets/work-report/?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = 'Excel indirilemedi.';
      try {
        const err = await response.json();
        message = err?.message || err?.error || message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `is_raporu_${date_from || 'baslangic'}_${date_to || 'bitis'}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

// Comment API methods
export const commentAPI = {
  // Get comments for a ticket
  getComments: async (ticketId) => {
    return await apiRequest(`/tickets/${ticketId}/comments/`);
  },

  // Add a new comment
  addComment: async (ticketId, commentText) => {
    return await apiRequest(`/tickets/${ticketId}/comments/`, {
      method: 'POST',
      body: JSON.stringify({
        comment_text: commentText
      })
    });
  },

  // Update a comment
  updateComment: async (ticketId, commentId, commentText) => {
    return await apiRequest(`/tickets/${ticketId}/comments/${commentId}/`, {
      method: 'PATCH',
      body: JSON.stringify({
        comment_text: commentText
      })
    });
  },

  // Delete a comment
  deleteComment: async (ticketId, commentId) => {
    return await apiRequest(`/tickets/${ticketId}/comments/${commentId}/`, {
      method: 'DELETE'
    });
  }
};

// Address API fonksiyonları
export const addressAPI = {
  // Adres listesi
  getAddresses: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.country) queryParams.append('country', params.country);
    if (params.city) queryParams.append('city', params.city);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);

    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await apiRequest(`/auth/addresses/${query}`);
  },

  // Adres detayı
  getAddress: async (id) => {
    return await apiRequest(`/auth/addresses/${id}/`);
  },

  // Adres oluştur
  createAddress: async (addressData) => {
    return await apiRequest('/auth/addresses/', {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  },

  // Adres güncelle
  updateAddress: async (id, addressData) => {
    return await apiRequest(`/auth/addresses/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(addressData),
    });
  },

  // Adres sil
  deleteAddress: async (id) => {
    return await apiRequest(`/auth/addresses/${id}/`, {
      method: 'DELETE',
    });
  },

  // Excel'den import
  importFromExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    return await apiRequest('/auth/addresses/import/', {
      method: 'POST',
      body: formData,
    });
  },

  // Excel şablonu indir
  getTemplate: () => {
    const token = tokenManager.getAccessToken();
    const url = `${API_BASE_URL}/auth/addresses/template/`;

    return fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(response => response.blob());
  }
};

// Sub-Agency API fonksiyonları
export const subAgencyAPI = {
  // Liste
  getSubAgencies: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.address_id) queryParams.append('address_id', params.address_id);
    if (params.country) queryParams.append('country', params.country);
    if (params.city) queryParams.append('city', params.city);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);

    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return await apiRequest(`/auth/sub-agencies/${query}`);
  },

  // Ekle
  createSubAgency: async (data) => {
    return await apiRequest('/auth/sub-agencies/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Güncelle
  updateSubAgency: async (id, data) => {
    return await apiRequest(`/auth/sub-agencies/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Sil
  deleteSubAgency: async (id) => {
    return await apiRequest(`/auth/sub-agencies/${id}/`, {
      method: 'DELETE',
    });
  },

  // Import
  importFromExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest('/auth/sub-agencies/import/', {
      method: 'POST',
      body: formData,
    });
  },

  // Template
  getTemplate: () => {
    const token = tokenManager.getAccessToken();
    const url = `${API_BASE_URL}/auth/sub-agencies/template/`;
    return fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => response.blob());
  },

};

/** Changelog / Innovations (Yenilikler) — persisted on backend */
export const changelogAPI = {
  list: async (category = null) => {
    const q =
      category && category !== 'All'
        ? `?category=${encodeURIComponent(category)}`
        : '';
    return await apiRequest(`/tickets/changelog/${q}`);
  },
  create: async (data) =>
    await apiRequest('/tickets/changelog/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: async (id, data) =>
    await apiRequest(`/tickets/changelog/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: async (id) =>
    await apiRequest(`/tickets/changelog/${id}/`, {
      method: 'DELETE',
    }),
  react: async (id, reactionType) =>
    await apiRequest(`/tickets/changelog/${id}/react/`, {
      method: 'POST',
      body: JSON.stringify({ reaction_type: reactionType }),
    }),
};

/** Microsoft 365 mail summary (Graph + Gemini on backend). Requires Helpdesk JWT + MS access token in body. */
export const microsoftMailAPI = {
  postMailSummaries: async (microsoftAccessToken) => {
    return apiRequest('/integrations/microsoft/mail-summary/', 'POST', {
      microsoft_access_token: microsoftAccessToken,
    });
  },
  postMailInbox: async ({ microsoftAccessToken, top = 20, nextLink = null }) => {
    const body = {
      microsoft_access_token: microsoftAccessToken,
      top,
    };
    if (nextLink) {
      body.next_link = nextLink;
    }
    return apiRequest('/integrations/microsoft/mail-inbox/', 'POST', body);
  },
};

export default {
  authAPI,
  usersAPI,
  referenceAPI,
  projectsAPI,
  ticketsAPI,
  changelogAPI,
  commentAPI,
  addressAPI,
  subAgencyAPI,
  microsoftMailAPI,
  tokenManager,
};
